import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Avatar,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
  UserOutlined,
  TeamOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FormOutlined,
  UploadOutlined,
  ImportOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useHrmsPolicy } from '@/hooks/useHrmsPolicy';
import employeeService, { employeeKeys, type EmployeeRecord } from '@/services/employee.service';
import departmentService from '@/services/department.service';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useAuthStore } from '@/store/authStore';
import { apiOriginForStaticAssets } from '@/config/apiBaseUrl';
import { useCurrency } from '@/contexts/CurrencyContext';

const { Text } = Typography;
const { Search } = Input;

function employeeListErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined;
    const msg = (data?.message || '').trim();
    const er = (data?.error || '').trim();
    const detail =
      msg && er && msg !== er ? `${msg} — ${er}` : msg || er || err.message || '';
    if (err.response?.status === 403) {
      return `${detail} If this persists, ask an admin to grant View HR, Company view, or User view (or re-login after your role was updated).`.trim();
    }
    if (err.response?.status === 400) {
      return detail || 'Bad request';
    }
    if (detail && /unknown column/i.test(detail)) {
      return `${detail} Run the HR employee migrations on the database (see backend migrations 110+).`;
    }
    return detail || `HTTP ${err.response?.status ?? ''}`.trim();
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

const EmployeesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { indiaPack } = useHrmsPolicy();
  const serverOrigin = useMemo(() => apiOriginForStaticAssets(), []);
  const { user } = useAuthStore();
  const { formatCurrency, baseCurrency } = useCurrency();
  const canDelete =
    Boolean(user?.is_super_admin) || Boolean(user?.permissions?.includes('hr.employee.delete'));
  const canCreate =
    Boolean(user?.is_super_admin) || Boolean(user?.permissions?.includes('hr.employee.create'));
  const [searchText, setSearchText] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importErrorsOpen, setImportErrorsOpen] = useState(false);

  const listParams = useMemo(
    () => ({
      department_id: departmentFilter || undefined,
      employment_status: statusFilter || undefined,
      employment_type: typeFilter || undefined,
      location_id: locationFilter || undefined,
      search: searchText.trim() || undefined,
    }),
    [departmentFilter, statusFilter, typeFilter, locationFilter, searchText]
  );

  const { data: employees = [], isLoading, error } = useQuery({
    queryKey: employeeKeys.list(listParams),
    queryFn: () => employeeService.list(listParams),
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments', 'selection', 'employees-list'],
    queryFn: () => departmentService.getForSelection(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: locations = [] } = useQuery({
    queryKey: hrmsKeys.locations(),
    queryFn: () => hrmsService.listLocations(),
    staleTime: 5 * 60 * 1000,
  });
  const locationLabelById = useMemo(
    () => new Map<string, string>(locations.map((l: any) => [l.id, `${l.location_code} — ${l.location_name}`])),
    [locations]
  );

  const pcrCountQuery = useQuery({
    queryKey: [...employeeKeys.all, 'pcr-pending-count'],
    queryFn: () => employeeService.listCompanyProfileChangeRequests('Pending'),
    staleTime: 60_000,
    retry: false,
  });
  const pendingPcr = pcrCountQuery.isError ? [] : (pcrCountQuery.data ?? []);

  const deptNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of departments) {
      m.set(d.id, d.department_name);
    }
    return m;
  }, [departments]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Active: 'success',
      Inactive: 'default',
      Onboarding: 'processing',
      Suspended: 'error',
      'On Leave': 'warning',
      Resigned: 'default',
      Terminated: 'error',
    };
    return colors[status] || 'default';
  };

  const stats = useMemo(() => {
    const total = employees.length;
    const active = employees.filter((e) => e.employment_status === 'Active').length;
    const onLeave = employees.filter((e) => e.employment_status === 'On Leave').length;
    const avgSalary =
      total > 0
        ? Math.round(
            employees.reduce((sum, e) => sum + Number(e.basic_salary ?? 0), 0) / total
          )
        : 0;
    return { total, active, onLeave, avgSalary };
  }, [employees]);

  const handleExport = async () => {
    try {
      const blob = await employeeService.exportCsvBlob(listParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees-${dayjs().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Export started');
    } catch {
      message.error('Export failed');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await employeeService.downloadImportTemplateAsFile();
      message.success('Import template downloaded');
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Template download failed');
    }
  };

  const importMut = useMutation({
    mutationFn: (file: File) => employeeService.importFromCsv(file),
    onSuccess: (result) => {
      const { created, updated, skipped, errors } = result;
      const applied = (created || 0) + (updated || 0);
      if (applied === 0 && errors?.length) {
        message.error(
          `Import failed — no rows applied (${errors.length} error${errors.length === 1 ? '' : 's'})`
        );
      } else {
        message.success(
          `Import done: ${created} created, ${updated} updated` +
            (skipped ? `, ${skipped} skipped` : '') +
            (errors?.length ? `, ${errors.length} row error(s)` : '')
        );
      }
      if (errors?.length) {
        setImportErrors(errors);
        setImportErrorsOpen(true);
      }
      void queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
    onError: (e: Error) => message.error(e.message || 'Import failed'),
  });

  const confirmDelete = (record: EmployeeRecord) => {
    Modal.confirm({
      title: `Delete ${record.first_name} ${record.last_name}?`,
      content: 'This soft-deletes the employee record.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        await employeeService.remove(record.id);
        message.success('Employee deleted');
        await queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      },
    });
  };

  const columns: ColumnsType<EmployeeRecord> = [
    {
      title: 'Employee',
      key: 'employee',
      fixed: 'left',
      width: 250,
      render: (_, record) => (
        <Space>
          <Avatar
            size="large"
            src={record.photo_url ? `${serverOrigin}${record.photo_url}` : undefined}
            icon={<UserOutlined />}
          />
          <Space direction="vertical" size={0}>
            <Text strong>{`${record.first_name} ${record.last_name}`}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.employee_code}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: '12px' }}>{record.email}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.mobile || '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Department',
      key: 'department',
      width: 150,
      render: (_, record) =>
        record.department_id ? deptNameById.get(record.department_id) || '—' : '—',
    },
    {
      title: 'Location',
      key: 'location',
      width: 180,
      ellipsis: true,
      render: (_, record) =>
        record.location_id ? (
          locationLabelById.get(record.location_id) || '—'
        ) : (
          <Text type="secondary">Not assigned</Text>
        ),
    },
    {
      title: 'Employment',
      key: 'employment',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">{record.employment_type || '—'}</Tag>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            Since {record.date_of_joining ? dayjs(record.date_of_joining).format('MMM YYYY') : '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Salary',
      key: 'salary',
      width: 120,
      render: (_, record) =>
        formatCurrency(Number(record.basic_salary ?? 0), record.currency || 'AED'),
    },
    {
      title: 'Status',
      dataIndex: 'employment_status',
      key: 'status',
      width: 120,
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 220,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/hr/employees/${record.id}`)}
          >
            Profile
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/hr/employees/${record.id}/edit`)}
          >
            Edit
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmDelete(record)}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Employees"
          subtitle={
            indiaPack
              ? 'HRMS people master — versioned profiles, salary via revisions, India statutory documents'
              : 'HRMS people master — versioned profiles, salary via revisions, UAE document attachments'
          }
          primaryActions={
            <Space wrap>
              <Button icon={<DownloadOutlined />} onClick={() => void handleExport()}>
                Export CSV
              </Button>
              {canCreate && (
                <Button icon={<ImportOutlined />} onClick={() => void handleDownloadTemplate()}>
                  Import template
                </Button>
              )}
              {canCreate && (
                <Upload
                  accept=".csv,text/csv"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    importMut.mutate(file);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={importMut.isPending}>
                    Import CSV
                  </Button>
                </Upload>
              )}
              <Button
                icon={<EnvironmentOutlined />}
                onClick={() => navigate('/hr/employees/location-assignment')}
              >
                Location assignment
              </Button>
              <Button icon={<ClockCircleOutlined />} onClick={() => navigate('/hr/employees/shift-assignment')}>
                Shift assignment
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/employees/new')}>
                Add employee
              </Button>
            </Space>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Advanced employee master"
        description={
          <span>
            Open a profile for {indiaPack ? 'India statutory documents' : 'UAE documents'}, salary history, and
            profile version trail. Edits to Active employees require approval. Use <strong>Import template</strong>{' '}
            then <strong>Import CSV</strong> for bulk load —
            match by <code>employee_code</code> (or email). <code>basic_salary</code> is export-only; set pay via Salary
            revisions. Dates use <code>YYYY-MM-DD</code>; use <code>department_code</code>,{' '}
            <code>location_code</code> and <code>reporting_to_employee_code</code> (not UUIDs). To move many
            employees between work locations use <strong>Location assignment</strong>; to change working hours use{' '}
            <strong>Shift assignment</strong>.
          </span>
        }
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Total Employees" value={stats.total} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Active" value={stats.active} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="On Leave" value={stats.onLeave} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() => navigate('/hr/profile-change-requests')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Pending profile requests"
              value={pendingPcr.length}
              prefix={<FormOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Click to open queue
            </Text>
          </Card>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg basic (from revisions)"
              value={formatCurrency(stats.avgSalary, baseCurrency?.currency_code || 'AED')}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {error && (
          <Typography.Paragraph type="danger" style={{ marginBottom: 16 }}>
            <Text strong>Could not load employees. </Text>
            {employeeListErrorMessage(error)}
          </Typography.Paragraph>
        )}
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
          <Space wrap>
            <Search
              placeholder="Search (server)…"
              allowClear
              style={{ width: 280 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(v) => setSearchText(v)}
              enterButton={<SearchOutlined />}
            />
            <Select
              placeholder="Department"
              allowClear
              style={{ width: 200 }}
              value={departmentFilter || undefined}
              onChange={(value) => setDepartmentFilter(value || '')}
            >
              {departments.map((d) => (
                <Select.Option key={d.id} value={d.id}>
                  {d.department_name}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 160 }}
              value={statusFilter || undefined}
              onChange={(value) => setStatusFilter(value || '')}
            >
              {['Active', 'Inactive', 'Onboarding', 'Suspended', 'On Leave', 'Resigned', 'Terminated'].map(
                (s) => (
                  <Select.Option key={s} value={s}>
                    {s}
                  </Select.Option>
                )
              )}
            </Select>
            <Select
              placeholder="Employment type"
              allowClear
              style={{ width: 160 }}
              value={typeFilter || undefined}
              onChange={(value) => setTypeFilter(value || '')}
            >
              {['Full-time', 'Part-time', 'Contract'].map((t) => (
                <Select.Option key={t} value={t}>
                  {t}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Location"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              value={locationFilter || undefined}
              onChange={(value) => setLocationFilter(value || '')}
              options={[
                { value: 'none', label: 'No location assigned' },
                ...locations.map((l: any) => ({
                  value: l.id,
                  label: `${l.location_code} — ${l.location_name}`,
                })),
              ]}
            />
          </Space>
        </Space>

        <Table<EmployeeRecord>
          columns={columns}
          dataSource={employees}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1600 }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      <Modal
        title="Import row errors"
        open={importErrorsOpen}
        onCancel={() => setImportErrorsOpen(false)}
        onOk={() => setImportErrorsOpen(false)}
        width={720}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Some rows were not applied. Fix the CSV and re-import (matching employee_code will update)."
        />
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          {importErrors.map((err, i) => (
            <Text key={i} style={{ display: 'block', marginBottom: 6 }}>
              {err}
            </Text>
          ))}
        </div>
      </Modal>
    </WorkspaceLayout>
  );
};

export default EmployeesPage;
